import type { Messages } from '../i18n/messages';

/**
 * Approvals module message catalog (Task 7d of the Fun Mode string sweep).
 * Every static chrome string in packages/app/src/modules/approvals/* is keyed
 * here. Server/dynamic data (approval titles, requester/approver refs, the
 * human-readable diff/summary content, task/PR backlinks, timestamps, counts,
 * env/repo/tag names) is never keyed — only interpolated via `{param}`.
 * NOT wired into `catalog.ts` yet (per task instructions) — `t()` falls back
 * to rendering the key itself at runtime until a later step wires it in.
 */
export const approvalsMessages: Messages = {
  // ---- List page chrome (ApprovalsListPage.tsx) ----
  'approvals.list.pageTitle': { en: 'Approvals', fun: 'Approval Central' },
  'approvals.list.pageDescription': {
    en: 'Review and act on approval requests across all kinds.',
    fun: 'Give the green light (or the boot) to whatever needs your stamp.',
  },
  'approvals.list.loadError': {
    en: "Couldn't load approvals",
    fun: 'Approvals ghosted us',
  },
  'approvals.list.retentionNotice': {
    en: 'Approval requests are automatically removed {days} days after they finish, and pending requests {days} days after they were opened.',
    fun: 'Approvals get swept away {days} days after they wrap, and stragglers still pending after {days} days get swept too.',
  },
  'approvals.list.status.all': { en: 'All statuses', fun: 'Any status' },
  'approvals.list.status.pending': { en: 'Pending', fun: 'Pending' },
  'approvals.list.status.processing': { en: 'Processing', fun: 'Cooking' },
  'approvals.list.status.completed': {
    en: 'Completed',
    fun: 'Done and dusted',
  },
  'approvals.list.status.rejected': { en: 'Rejected', fun: 'Denied' },
  'approvals.list.status.failed': { en: 'Failed', fun: 'Flopped' },
  'approvals.list.viewAriaLabel': {
    en: 'Approval view',
    fun: 'Approval view',
  },
  'approvals.list.view.active': { en: 'Active', fun: 'Active' },
  'approvals.list.view.archived': { en: 'Archived', fun: 'Archived' },
  'approvals.list.templateFilterLabel': { en: 'Template', fun: 'Flavor' },
  'approvals.list.allTemplates': { en: 'All templates', fun: 'Any flavor' },
  'approvals.list.statusFilterLabel': { en: 'Status', fun: 'Status' },
  'approvals.list.needsMyApproval': { en: 'Needs my approval', fun: 'On me' },
  'approvals.list.loadingRows': {
    en: 'Loading approval requests…',
    fun: 'Rounding up the requests…',
  },
  'approvals.list.emptyTitle.archived': {
    en: 'No archived approvals',
    fun: 'Archive is empty',
  },
  'approvals.list.emptyTitle.default': {
    en: 'No approval requests',
    fun: 'Nothing to approve — go relax',
  },
  'approvals.list.emptyDescription.default': {
    en: 'When someone triggers a release or promotion, it shows up here for review.',
    fun: 'The moment someone ships or promotes something, it lands right here.',
  },
  'approvals.list.emptyDescription.archived': {
    en: 'Approvals you archive will show up here.',
    fun: 'Stash your handled approvals here for safekeeping.',
  },
  'approvals.list.emptyDescription.needsMine': {
    en: 'Nothing is waiting on your approval right now.',
    fun: 'Your queue is clear — nothing needs your blessing.',
  },
  'approvals.list.column.template': { en: 'Template', fun: 'Flavor' },
  'approvals.list.column.request': { en: 'Request', fun: 'The ask' },
  'approvals.list.column.requester': { en: 'Requester', fun: 'Asker' },
  'approvals.list.column.approvers': { en: 'Approvers', fun: 'Deciders' },
  'approvals.list.column.created': { en: 'Created', fun: 'Born on' },
  'approvals.list.column.status': { en: 'Status', fun: 'Status' },
  'approvals.list.column.result': { en: 'Result', fun: 'Outcome' },
  'approvals.list.approvedBy': { en: 'Approved by', fun: 'Blessed by' },
  'approvals.list.rejectedBy': { en: 'Rejected by', fun: 'Shut down by' },
  'approvals.list.viewResult': { en: 'View result', fun: 'See the receipts' },
  'approvals.list.selectAllOnPage': {
    en: 'Select all on this page',
    fun: 'Grab everything on this page',
  },
  'approvals.list.selectRequest': {
    en: 'Select request',
    fun: 'Grab this one',
  },
  'approvals.list.approveTooltip': { en: 'Approve', fun: 'Approve' },
  'approvals.list.rejectTooltip': {
    en: 'Reject request',
    fun: 'Reject request',
  },
  'approvals.list.unarchiveTooltip': { en: 'Unarchive', fun: 'Unarchive' },
  'approvals.list.archiveTooltip': { en: 'Archive', fun: 'Archive' },
  'approvals.list.approverTooltip': {
    en: 'Any one of these can approve',
    fun: 'Any of these folks can wave it through',
  },
  'approvals.list.autoNone': { en: 'auto / none', fun: 'auto / none' },
  'approvals.list.moreCount': { en: '+{count} more', fun: '+{count} more' },
  'approvals.list.notify.approved': {
    en: 'Request approved',
    fun: 'Approved. Nailed it.',
  },
  'approvals.list.notify.rejected': {
    en: 'Request rejected',
    fun: 'Rejected. Sent packing.',
  },
  'approvals.list.notify.actionFailed': {
    en: 'Could not {action} request',
    fun: 'Could not {action} request — try again',
  },
  'approvals.list.notify.archived': {
    en: 'Approval archived',
    fun: 'Tucked into the archive',
  },
  'approvals.list.notify.unarchived': {
    en: 'Approval unarchived',
    fun: 'Back from the archive',
  },
  'approvals.list.notify.archiveFailed': {
    en: 'Could not archive',
    fun: 'Archiving didn’t stick',
  },
  'approvals.list.notify.unarchiveFailed': {
    en: 'Could not unarchive',
    fun: 'Unarchiving didn’t stick',
  },
  'approvals.list.verb.approve': { en: 'Approve', fun: 'Approve' },
  'approvals.list.verb.reject': { en: 'Reject', fun: 'Reject' },
  'approvals.list.verb.archive': { en: 'Archive', fun: 'Archive' },
  'approvals.list.verb.unarchive': { en: 'Unarchive', fun: 'Unarchive' },
  'approvals.list.noun.singular': { en: 'request', fun: 'request' },
  'approvals.list.noun.plural': { en: 'requests', fun: 'requests' },
  'approvals.list.notify.bulkSuccess': {
    en: '{verb}: {count} {noun}',
    fun: '{verb}: {count} {noun} — nice',
  },
  'approvals.list.notify.bulkAllFailed': {
    en: '{verb} failed for all {count} requests',
    fun: '{verb} whiffed on all {count} requests',
  },
  'approvals.list.notify.bulkPartial': {
    en: '{verb}: {ok} succeeded, {failed} failed',
    fun: '{verb}: {ok} in, {failed} bailed',
  },
  'approvals.list.confirm.approveTitle': {
    en: 'Approve {count} {noun}?',
    fun: 'Green-light {count} {noun}?',
  },
  'approvals.list.confirm.approveDescription': {
    en: 'This runs each selected request’s workflow and cannot be undone.',
    fun: 'This kicks off every selected workflow — no take-backs.',
  },
  'approvals.list.confirm.rejectTitle': {
    en: 'Reject {count} {noun}?',
    fun: 'Shut down {count} {noun}?',
  },
  'approvals.list.confirm.rejectDescription': {
    en: 'The selected requests will be rejected. This cannot be undone.',
    fun: 'The selected requests get the boot — no take-backs.',
  },
  'approvals.list.confirm.archiveTitle': {
    en: 'Archive {count} {noun}?',
    fun: 'Box up {count} {noun}?',
  },
  'approvals.list.confirm.archiveDescription': {
    en: 'Archived approvals move to the Archived view; you can unarchive later.',
    fun: 'Off to the Archived view — you can bring them back anytime.',
  },
  'approvals.list.confirm.unarchiveTitle': {
    en: 'Unarchive {count} {noun}?',
    fun: 'Unbox {count} {noun}?',
  },
  'approvals.list.confirm.unarchiveDescription': {
    en: 'These approvals return to the Active view.',
    fun: 'Back to the Active view they go.',
  },

  // ---- Detail page chrome (ApprovalDetailsPage.tsx) ----
  'approvals.detail.backToNotifications': {
    en: 'Back to notifications',
    fun: 'Back to the notification pile',
  },
  'approvals.detail.backToApprovals': {
    en: 'Back to approvals',
    fun: 'Back to the approval pile',
  },
  'approvals.detail.loading': {
    en: 'Loading request…',
    fun: 'Digging up the request…',
  },
  'approvals.detail.loadError': {
    en: "Couldn't load request",
    fun: "Couldn't dig up the request",
  },
  'approvals.detail.notFound': {
    en: 'Request not found.',
    fun: 'That request vanished into thin air.',
  },
  'approvals.detail.field.template': { en: 'Template', fun: 'Flavor' },
  'approvals.detail.field.status': { en: 'Status', fun: 'Status' },
  // Synthetic status for a release-all that completed but left some services out.
  'approvals.status.partial': {
    en: 'partial completed',
    fun: 'partial completed',
  },
  'approvals.detail.field.approvedBy': {
    en: 'Approved by',
    fun: 'Blessed by',
  },
  'approvals.detail.field.rejectedBy': {
    en: 'Rejected by',
    fun: 'Shut down by',
  },
  'approvals.detail.field.requester': { en: 'Requester', fun: 'Asker' },
  'approvals.detail.field.created': { en: 'Created', fun: 'Born on' },
  'approvals.detail.field.originatingTask': {
    en: 'Originating task',
    fun: 'Where it all started',
  },
  'approvals.detail.viewTask': {
    en: 'View task',
    fun: 'Peek at the task',
  },
  'approvals.detail.field.approvers': { en: 'Approvers', fun: 'Deciders' },
  'approvals.detail.noApprovers': {
    en: 'No approvers resolved.',
    fun: 'Nobody’s on the hook to approve this.',
  },
  'approvals.detail.requestedBy': {
    en: 'Requested by',
    fun: 'Asked for by',
  },
  'approvals.detail.reject': { en: 'Reject', fun: 'Reject' },
  'approvals.detail.approve': { en: 'Approve', fun: 'Approve' },
  'approvals.detail.dismiss': { en: 'Dismiss', fun: 'Shoo away' },
  'approvals.detail.dismissNotDismissedTitle': {
    en: 'Not dismissed',
    fun: 'Still here',
  },
  'approvals.detail.dismissStillExists': {
    en: 'The GitHub release still exists, so this approval was kept. Dismiss only removes approvals whose release has been deleted.',
    fun: 'The GitHub release is still around, so this approval stays. Shoo away only clears approvals whose release is gone.',
  },
  'approvals.detail.cardRequest': { en: 'Request', fun: 'The ask' },
  'approvals.detail.cardSummary': { en: 'Summary', fun: 'The gist' },
  'approvals.detail.resultLabel': { en: 'Result:', fun: 'Outcome:' },

  // ---- Bulk action bar (BulkActionBar.tsx) ----
  'approvals.bulkActionBar.selectedCount': {
    en: '{count} selected',
    fun: '{count} picked',
  },
  'approvals.bulkActionBar.approve': { en: 'Approve', fun: 'Approve' },
  'approvals.bulkActionBar.approveCount': {
    en: 'Approve ({count})',
    fun: 'Approve ({count})',
  },
  'approvals.bulkActionBar.reject': { en: 'Reject', fun: 'Reject' },
  'approvals.bulkActionBar.rejectCount': {
    en: 'Reject ({count})',
    fun: 'Reject ({count})',
  },
  'approvals.bulkActionBar.archive': { en: 'Archive', fun: 'Archive' },
  'approvals.bulkActionBar.unarchive': {
    en: 'Unarchive',
    fun: 'Unarchive',
  },
  'approvals.bulkActionBar.clear': { en: 'Clear', fun: 'Never mind' },

  // ---- gitops-config renderer (renderers/gitopsConfig.tsx) ----
  'approvals.gitopsConfig.rowSummary': {
    en: '{overlay} · {count} variable(s)',
    fun: '{overlay} · {count} tweak(s)',
  },
  'approvals.gitopsConfig.resourceLabel': { en: 'Resource', fun: 'Resource' },
  'approvals.gitopsConfig.overlayLabel': { en: 'Overlay', fun: 'Overlay' },
  'approvals.gitopsConfig.configChangesLabel': {
    en: 'Config changes',
    fun: 'What’s changing',
  },
  'approvals.gitopsConfig.noChanges': {
    en: 'No changes',
    fun: 'Nothing changed',
  },
  'approvals.gitopsConfig.removed': {
    en: 'Removed: {keys}',
    fun: 'Yeeted: {keys}',
  },

  // ---- gitops-resource renderer (renderers/gitopsResource.tsx) ----
  'approvals.gitopsResource.rowSummary': {
    en: '{overlay} · {summary}',
    fun: '{overlay} · {summary}',
  },
  'approvals.gitopsResource.resourceLabel': {
    en: 'Resource',
    fun: 'Resource',
  },
  'approvals.gitopsResource.overlayLabel': { en: 'Overlay', fun: 'Overlay' },
  'approvals.gitopsResource.changesLabel': {
    en: 'Changes',
    fun: 'What’s changing',
  },
  'approvals.gitopsResource.noChangesSelected': {
    en: 'No changes selected',
    fun: 'Nothing picked',
  },
  'approvals.gitopsResource.cpu': { en: 'CPU: {value}', fun: 'CPU: {value}' },
  'approvals.gitopsResource.memory': {
    en: 'Memory: {value}',
    fun: 'Memory: {value}',
  },
  'approvals.gitopsResource.cpuRequest': {
    en: 'CPU req: {value}',
    fun: 'CPU req: {value}',
  },
  'approvals.gitopsResource.cpuLimit': {
    en: 'CPU limit: {value}',
    fun: 'CPU limit: {value}',
  },
  'approvals.gitopsResource.memRequest': {
    en: 'Mem req: {value}',
    fun: 'Mem req: {value}',
  },
  'approvals.gitopsResource.memLimit': {
    en: 'Mem limit: {value}',
    fun: 'Mem limit: {value}',
  },
  'approvals.gitopsResource.min': { en: 'Min: {value}', fun: 'Min: {value}' },
  'approvals.gitopsResource.max': { en: 'Max: {value}', fun: 'Max: {value}' },

  // ---- gitops-secrets renderer (renderers/gitopsSecrets.tsx) ----
  'approvals.gitopsSecrets.rowSummary': {
    en: '{overlay} · {count} secret ref(s)',
    fun: '{overlay} · {count} secret stash(es)',
  },
  'approvals.gitopsSecrets.resourceLabel': {
    en: 'Resource',
    fun: 'Resource',
  },
  'approvals.gitopsSecrets.overlayLabel': { en: 'Overlay', fun: 'Overlay' },
  'approvals.gitopsSecrets.secretRefsLabel': {
    en: 'Secret refs',
    fun: 'Secret stash',
  },
  'approvals.gitopsSecrets.valuesManaged': {
    en: 'Values are managed in Azure Key Vault.',
    fun: 'The actual values live safely in Azure Key Vault.',
  },

  // ---- release-all renderer (renderers/releaseAll.tsx) ----
  'approvals.releaseAll.rowSummary': {
    en: 'Release {count} service(s) → {env}',
    fun: 'Ship {count} service(s) → {env}',
  },
  'approvals.releaseAll.environmentLabel': {
    en: 'Environment',
    fun: 'Environment',
  },
  'approvals.releaseAll.servicesLabel': { en: 'Services', fun: 'Services' },
  'approvals.releaseAll.sharedActionsLabel': {
    en: 'Shared Actions version',
    fun: 'Shared Actions version',
  },
  'approvals.releaseAll.sharedActionsValue': {
    en: 'will be set to {version} on approval',
    fun: 'jumps to {version} on approval',
  },
  'approvals.releaseAll.failedTitle': {
    en: 'Not released ({count})',
    fun: 'Left behind ({count})',
  },
  'approvals.releaseAll.failedViewRun': {
    en: 'View CI run',
    fun: 'View CI run',
  },

  // ---- release-publish renderer (renderers/releasePublish.tsx) ----
  'approvals.releasePublish.environmentLabel': {
    en: 'Environment',
    fun: 'Environment',
  },
  'approvals.releasePublish.repositoryLabel': {
    en: 'Repository',
    fun: 'Repository',
  },
  'approvals.releasePublish.tagLabel': { en: 'Tag', fun: 'Tag' },

  // ---- rollout-restart renderer (renderers/rolloutRestart.tsx) ----
  'approvals.rolloutRestart.rowSummary': {
    en: 'Rolling restart · {workloads}',
    fun: 'Reboot party · {workloads}',
  },
  'approvals.rolloutRestart.environmentLabel': {
    en: 'Environment',
    fun: 'Environment',
  },
  'approvals.rolloutRestart.serviceLabel': { en: 'Service', fun: 'Service' },
  'approvals.rolloutRestart.workloadsLabel': {
    en: 'Workloads',
    fun: 'Workloads',
  },
  'approvals.rolloutRestart.effectLabel': {
    en: 'Effect',
    fun: 'What happens',
  },
  'approvals.rolloutRestart.effectDescription': {
    en: 'Rolling restart (no pods killed at once) on approval',
    fun: 'A rolling restart — one pod at a time, nothing dies all at once',
  },

  // ---- shared-actions-version renderer (renderers/sharedActionsVersion.tsx) ----
  'approvals.sharedActionsVersion.title': {
    en: 'Shared Actions release',
    fun: 'Shared Actions glow-up',
  },
  'approvals.sharedActionsVersion.releaseChangeLabel': {
    en: 'Release change',
    fun: 'Version jump',
  },

  // ---- usvc-migration-run renderer (renderers/usvcMigrationRun.tsx) ----
  'approvals.usvcMigrationRun.environmentLabel': {
    en: 'Environment',
    fun: 'Environment',
  },
  'approvals.usvcMigrationRun.branchLabel': { en: 'Branch', fun: 'Branch' },
  'approvals.usvcMigrationRun.migrationFileLabel': {
    en: 'Migration file',
    fun: 'Migration file',
  },
  'approvals.usvcMigrationRun.runLabel': {
    en: 'GitHub run',
    fun: 'GitHub run',
  },
  'approvals.usvcMigrationRun.viewRun': {
    en: 'View run',
    fun: 'Peek at the run',
  },

  // ---- GitHub release preview (renderers/ReleaseInfoCard.tsx) ----
  'approvals.releaseInfoCard.loading': {
    en: 'Loading release from GitHub…',
    fun: 'Fetching the release from GitHub…',
  },
  'approvals.releaseInfoCard.unauthorized': {
    en: 'Sign in with GitHub to preview the release here.',
    fun: 'Sign in with GitHub to peek at the release.',
  },
  'approvals.releaseInfoCard.loadError': {
    en: "Couldn't load the release from GitHub.",
    fun: "GitHub wouldn't hand over the release.",
  },
  'approvals.releaseInfoCard.viewReleasesOnGithub': {
    en: 'View releases on GitHub',
    fun: 'See the releases on GitHub',
  },
  'approvals.releaseInfoCard.viewReleaseOnGithub': {
    en: 'View release on GitHub',
    fun: 'See the release on GitHub',
  },
  'approvals.releaseInfoCard.viewOnGithub': {
    en: 'View on GitHub',
    fun: 'See it on GitHub',
  },
  'approvals.releaseInfoCard.byAuthor': {
    en: 'by {author}',
    fun: 'by {author}',
  },
  'approvals.releaseInfoCard.fallbackTitle': {
    en: 'Release',
    fun: 'Release',
  },
  'approvals.releaseInfoCard.cardTitle': {
    en: 'GitHub release',
    fun: 'GitHub release',
  },
  'approvals.releaseInfoCard.cardTitleAll': {
    en: 'GitHub releases',
    fun: 'GitHub releases',
  },
  'approvals.releaseInfoCard.statusDraft': { en: 'Draft', fun: 'Draft' },
  'approvals.releaseInfoCard.statusPrerelease': {
    en: 'Pre-release',
    fun: 'Pre-release',
  },
  'approvals.releaseInfoCard.statusPublished': {
    en: 'Published',
    fun: 'Published',
  },

  // ---- github-repo-create renderer (renderers/githubRepoCreate.tsx) ----
  'approvals.githubRepoCreate.rowSummary': {
    en: 'New GitHub repository',
    fun: 'A brand-new repo',
  },
  'approvals.githubRepoCreate.repositoryLabel': {
    en: 'Repository',
    fun: 'Repository',
  },
  'approvals.githubRepoCreate.visibilityLabel': {
    en: 'Visibility',
    fun: 'Visibility',
  },
  'approvals.githubRepoCreate.visibilityValue': {
    en: 'Private — an admin can widen it later',
    fun: 'Private — an admin can widen it later',
  },
  'approvals.githubRepoCreate.descriptionLabel': {
    en: 'Description',
    fun: 'Description',
  },
  'approvals.githubRepoCreate.propertiesLabel': {
    en: 'Custom properties',
    fun: 'Custom properties',
  },
  'approvals.githubRepoCreate.propertiesDefaults': {
    en: 'Organization defaults',
    fun: 'Org defaults',
  },
};
